import { withRouter } from "react-router-dom"
import React, { useState } from "react"
import {
  Card,
  Button,
  CardBody,
  Row,
  Col,
} from "reactstrap"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"
import ToolkitProvider, {
  CSVExport,
  Search,
} from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import * as moment from "moment"
import TableLoader from "components/Common/TableLoader"
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css"
import axios from "axios"
import { API_URL } from "constants/constants"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

function Table(props) {
  let { data, total, toDate, fromDate } = props
  
  // Define columns - keeping the original structure but optimizing formatters
  const columns = [
    {
      dataField: "id",
      text: "ID",
      sort: true,
    },
    {
      dataField: "date_registered",
      text: "Date Registered",
      sort: true,
      formatter: (cell, row) => {
        if (!row?.date_registered) return '';
        const date = new Date(row.date_registered);
        const formattedDate = date.toLocaleString("en-GB", {
          timeZone: "CAT",
          hour12: true,
        });
        return formattedDate.replace(/(am|pm)/gi, match => match.toUpperCase());
      },
    },
    {
      dataField: "end_user",
      text: "End User",
      sort: true,
    },
    {
      dataField: "nrc_number",
      text: "NRC Number",
      sort: true,
    },
    {
      dataField: "referral_source",
      text: "Ref Source",
      sort: true,
    },
    {
      dataField: "referred_by_code",
      text: "Referral Code",
      sort: false,
    },
    {
      dataField: "is_req_through_popup",
      text: "Popup Registered",
      sort: true,
      formatter: (cell, row) => (
        <>{row?.is_req_through_popup == 1 ? "True" : "False"}</>
      ),
    },
    {
      dataField: "bets",
      text: "Bets",
      sort: true,
      formatter: (cell, row) => {
        const value = row.bets ? Number(row.bets.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "current_balance",
      text: "Current Balance",
      sort: true,
      formatter: (cell, row) => {
        const value = row?.current_balance ? 
          Number(row.current_balance.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "signup_freebet_bonus",
      text: "Signup Balance",
      sort: true,
      formatter: (cell, row) => (
        <>
          {Number(row?.signup_freebet_bonus?.toFixed(2) ?? 0).toLocaleString(
            "en-GB"
          )}
        </>
      ),
    },
    {
      dataField: "total_stake",
      text: "Total Stake",
      sort: true,
      formatter: (cell, row) => {
        const value = row.total_stake ? 
          Number(row.total_stake.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "effective_stake",
      text: "Effective Stake",
      sort: true,
      formatter: (cell, row) => {
        const value = row.effective_stake ? 
          Number(row.effective_stake.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "boost",
      text: "Boost",
      sort: true,
      formatter: (cell, row) => {
        const value = row.boost ? 
          Number(row.boost.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "wht",
      text: "WHT",
      sort: true,
      formatter: (cell, row) => {
        const value = row.wht ? 
          Number(parseFloat(row.wht).toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "net_payout",
      text: "Net Payout",
      sort: true,
      formatter: (cell, row) => {
        const value = row.net_payout ? 
          Number(row.net_payout.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "total_won_stake",
      text: "Total Won Stakes",
      sort: true,
      formatter: (cell, row) => {
        return row.total_won_stake ? row.total_won_stake.toFixed(2) : '0';
      },
    },
    {
      dataField: "total_lost_stake",
      text: "Total Lost",
      sort: true,
      formatter: (cell, row) => {
        return row.total_lost_stake ? row.total_lost_stake.toFixed(2) : '0';
      },
    },
    {
      dataField: "GGR",
      text: "GGR",
      sort: true,
      formatter: (cell, row) => {
        const value = row.GGR ? 
          Number(row.GGR.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
    {
      dataField: "GGR_ratio",
      text: "GGR Ratio",
      sort: true,
      formatter: (cell, row) => {
        const value = row.GGR_ratio ? 
          Number(row.GGR_ratio.toFixed(2)).toLocaleString("en-GB") : '0';
        return <>{value}</>;
      },
    },
  ];

  const defaultSorted = [
    {
      dataField: "id",
      order: "desc",
    },
  ];

  const { SearchBar } = Search;
  
  // Define pagination settings
  const pagination = paginationFactory({
    sizePerPage: 50,
    lastPageText: ">>",
    firstPageText: "<<",
    nextPageText: ">",
    prePageText: "<",
    totalSize: total || 0,
    alwaysShowAllBtns: true,
    hideSizePerPage: true,
    hidePageListOnlyOnePage: true,
  });
  
  // Export CSV button component
  const MyExportCSV = (props) => {
    const handleClick = () => {
      if (typeof props.setLoadingMessage === 'function') {
        props.setLoadingMessage("1");
      }
      
      axios({
        method: "get",
        url: `${API_URL}/admin/reports/customer-reports?to=${toDate}&from=${fromDate}&unsetPagination=true`,
      })
        .then(function (response) {
          if (response.data && response.data.success) {
            toastr.success(response.data.message) 
          } else {
            toastr.error("An error occurred while processing your request.") 
          }
          if (typeof props.setLoadingMessage === 'function') {
            props.setLoadingMessage("0");
          }
        })
        .catch(function (error) {
          console.error("Error fetching reports:", error) 
          toastr.error("Failed to fetch reports. Please try again.") 
          if (typeof props.setLoadingMessage === 'function') {
            props.setLoadingMessage("0");
          }
        });
    };
    
    return (
      <div>
        <button className="btn btn-primary" onClick={handleClick}>
          Download
        </button>
      </div>
    );
  };

  return (
    <>
      <Card>
        <CardBody>
          <div className="mb-4 h4 card-title">{"Customers Report"}</div>
          {props.loadingMessage && props.loadingMessage == "1" ? (
            <TableLoader />
          ) : (
            <PaginationProvider
              pagination={pagination}
              keyField="id"
              columns={columns}
              data={data || []}
            >
              {({ paginationProps, paginationTableProps }) => (
                <ToolkitProvider
                  keyField="id"
                  data={data || []}
                  columns={columns}
                  bootstrap4
                  search
                  exportCSV={{
                    onlyExportFiltered: true,
                    exportAll: false,
                    fileName: `Customers-Report-${moment(new Date()).format(
                      "DD/MM/YYYY"
                    )}.csv`,
                  }}
                >
                  {toolkitProps => (
                    <React.Fragment>
                      <Row className="mb-2">
                        <Col sm="6">
                          <Row>
                            <Col sm="6">
                              <div className="search-box me-2 mb-2 d-inline-block">
                                <div className="position-relative">
                                  <SearchBar {...toolkitProps.searchProps} />
                                  <i className="bx bx-search-alt search-icon" />
                                </div>
                              </div>
                            </Col>
                            <Col sm="6">
                              <div>
                                <MyExportCSV 
                                  {...toolkitProps.csvProps} 
                                  setLoadingMessage={props.setLoadingMessage}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col sm={6}></Col>
                      </Row>
                      <Row>
                        <Col xl="12">
                          <div className="table-responsive">
                            <BootstrapTable
                              id="table-to-xls"
                              keyField="id"
                              responsive
                              bordered={false}
                              striped={false}
                              defaultSorted={defaultSorted}
                              classes="table align-middle table-nowrap table-check"
                              headerWrapperClasses="table-light"
                              {...toolkitProps.baseProps}
                              {...paginationTableProps}
                              rowClasses="mytable-hover-row"
                            />
                          </div>
                        </Col>
                      </Row>
                      <div className="" id="pagination-id"></div>
                    </React.Fragment>
                  )}
                </ToolkitProvider>
              )}
            </PaginationProvider>
          )}
        </CardBody>
      </Card>
    </>
  );
}

export default withRouter(Table);